/**
 * Constantes do catálogo. As opções de filtro não vêm da API (o `list-products` não devolve categorias nem faixa de
 * preço): são as listas fixas da loja (o `src/mocks` de filtros saiu na etapa das telas), com os valores já
 * decodificados (`Sem Pó`, não `Sem%20Pó`), porque a URL agora é montada pelo `URLSearchParams`.
 */

/**
 * Tempo em que os dados do catálogo valem sem nova busca (o mesmo do queryClient da loja). Explícito nos hooks para o
 * dado buscado no servidor (`initialData`) não ser rebuscado na montagem.
 */
export const CATALOG_STALE_TIME = 1000 * 60 * 5

/** Imagem do card quando o produto não tem imagem cadastrada (host liberado no `next.config.js`). */
export const PRODUCT_PLACEHOLDER_IMAGE = 'https://placehold.co/600x400/png'

/** Produtos por página da listagem `/produtos` (o scroll infinito pede a próxima página quando vem cheia). */
export const PRODUCT_LIST_PAGE_SIZE = 9

/** Ids das vitrines (`listGroup`) usadas pela loja em `product/get-by-list-group/{id}`. */
export const SHOWCASE_LIST_GROUPS = {
    /** Mais vendidos da home. */
    HOME_BEST_SELLERS: '64121bd979699db3b3a1cd1f',
    /** Mais vendidos da PDP. */
    PRODUCT_BEST_SELLERS: '63fcfc29822be458050f18dc',
} as const

/**
 * Material (vai em `feature`, filtrado pelas tags do produto). A API compara a tag sem caixa, mas com acento: o valor é
 * o da tag cadastrada ("Látex"), como o rótulo que o filtro legado mandava.
 */
export const PRODUCT_MATERIAL_OPTIONS = [
    { name: 'látex', value: 'látex' },
    { name: 'nitrilo', value: 'nitrilo' },
] as const

/** Características (vão em `feature`, filtradas pelas tags do produto). */
export const PRODUCT_CHARACTERISTIC_OPTIONS = [
    { name: 'sem pó', value: 'Sem Pó' },
    { name: 'com hidratante', value: 'Com Hidratante' },
    { name: 'pouco pó', value: 'Pouco Pó' },
    { name: 'microtexturizada', value: 'microtexturizada' },
    { name: 'lisa', value: 'lisa' },
] as const

/** Atalhos de destaque (vão em `feature`). */
export const PRODUCT_FEATURE_OPTIONS = [
    { name: 'novidades', value: 'novidades' },
    { name: 'mais vendidos', value: 'mais vendidos' },
    { name: 'Glöfi selection', value: 'glöfi selection' },
] as const

/** Categorias (vão em `category`). */
export const PRODUCT_CATEGORY_OPTIONS = [
    { name: 'dentista e estética', value: 'dentista-e-estetica' },
    { name: 'vestuário', value: 'vestuario' },
    { name: 'máscaras', value: 'mascaras' },
    { name: 'luvas', value: 'luvas' },
    { name: 'sacolas', value: 'sacolas' },
    { name: 'biossegurança', value: 'biosseguranca' },
    { name: 'equipamentos', value: 'equipamentos' },
    { name: 'ofertas', value: 'ofertas' },
] as const

/** Tamanhos (vão em `size`, comparados ao `productSize`). */
export const PRODUCT_SIZE_OPTIONS = [
    { name: 'EP', value: 'EP' },
    { name: 'P', value: 'P' },
    { name: 'M', value: 'M' },
    { name: 'G', value: 'G' },
    { name: 'EG', value: 'EG' },
] as const

/** Cores (vão em `color`, comparadas aos atributos do produto); `bgColor` é a amostra exibida. */
export const PRODUCT_COLOR_OPTIONS = [
    { bgColor: '#ffdfb6', value: 'yellow' },
    { bgColor: '#7ec3aa', value: 'green' },
    { bgColor: '#ffb09e', value: 'orange' },
    { bgColor: '#ff7e79', value: 'red' },
    { bgColor: '#eca9b6', value: 'pink' },
    { bgColor: '#8f8c89', value: 'black' },
    { bgColor: '#dfcaff', value: 'purple' },
    { bgColor: '#8897c3', value: 'blue' },
    { bgColor: '#ffffff', value: 'white' },
] as const

/** Faixas de preço do filtro (`null` = sem limite), com os rótulos que a loja mostra. */
export const PRODUCT_PRICE_RANGES = [
    { label: 'até R$30', from: 0, to: 30 },
    { label: 'de R$30 a R$40', from: 30, to: 40 },
    { label: 'de R$40 a R$50', from: 40, to: 50 },
    { label: 'a partir de R$50', from: 50, to: null },
] as const
